import {createCarton,disposeScene} from './lb-orbit-models.js';
import {cartonFlavours,featuredFirst} from './lb-orbit-flavours.js';
import {orbitPoses,smooth,clamp} from './lb-orbit-choreography.js';

function setup(root){
if(root.orbitCleanup)root.orbitCleanup();
const aborter=new AbortController(),signal=aborter.signal;
const stage=root.querySelector('.scene'),canvas=root.querySelector('canvas');
const artworkUrls=JSON.parse(root.querySelector('[data-orbit-artwork]').textContent);
const poses=orbitPoses;
const chapters=[...root.querySelectorAll('.scene-copy')];
const flavours=[...root.querySelectorAll('[data-flavour]')];
const motion=document.querySelector('[data-motion-toggle]'),progress=root.querySelector('.scene-progress');
const poster=root.querySelector('.scene-poster'),wash=root.querySelector('.scene-wash'),word=root.querySelector('.scene-word');
const reduced=matchMedia('(prefers-reduced-motion: reduce)');
let off=false;try{off=sessionStorage.getItem('lb-concept-motion')==='off';}catch{}
const artwork=cartonFlavours;
let allModels=[];
let renderer,scene,camera,models=[],textures=[],ready=false,failed=false,loading=false,disposed=false,visible=true;
let raf=0,current=0,selected=0,lastTime=0,chapter=-1;
let pointer={x:0,y:0},pointerTarget={x:0,y:0};
const allowed=()=>!off&&!reduced.matches&&!failed;
const live=()=>allowed()&&ready;
const bounds=()=>{
 const top=parseFloat(getComputedStyle(stage).top)||parseFloat(getComputedStyle(root).getPropertyValue('--header'))||0;
 return {start:root.getBoundingClientRect().top+scrollY-top,span:Math.max(1,root.clientHeight-stage.clientHeight)};
};
function setChapter(index){
 if(chapter===index)return;chapter=index;
 chapters.forEach((copy,i)=>{copy.classList.toggle('is-current',i===index);copy.setAttribute('aria-hidden',String(i!==index));});
}

function sync(){
 root.classList.toggle('live',live());root.dataset.state=failed?'fallback':live()?'ready':loading?'loading':'static';
 if(motion){
  motion.hidden=false;motion.querySelector('span').textContent=reduced.matches||failed||off?'off':'on';
  motion.setAttribute('aria-label',reduced.matches?'Motion disabled by your system preference':allowed()?'Turn motion off':'Turn motion on');
  motion.setAttribute('aria-pressed',String(!allowed()));motion.disabled=failed||reduced.matches;
 }
 if(!live()){cancelAnimationFrame(raf);raf=0;setChapter(0);}else schedule();
}
function fail(error){failed=true;root.dataset.failure=error.message;sync();}
function resize(){
 if(!renderer)return;
 const width=canvas.clientWidth,height=canvas.clientHeight;
 renderer.setSize(width,height,false);camera.aspect=width/Math.max(1,height);
 camera.position.z=Math.max(16,10.3/(2*Math.tan(Math.PI/10)*camera.aspect));
 camera.updateProjectionMatrix();schedule();
}
async function load(){
 if(loading||ready||!allowed()||disposed)return;loading=true;sync();
 try{
  const T=await import('./lb-orbit-three.js');if(disposed)return;
  renderer=new T.WebGLRenderer({canvas,antialias:true,alpha:true,powerPreference:'low-power'});
  renderer.setPixelRatio(Math.min(devicePixelRatio,1.75));renderer.outputColorSpace=T.SRGBColorSpace;
  renderer.toneMapping=T.ACESFilmicToneMapping;renderer.toneMappingExposure=1.17;
  scene=new T.Scene();camera=new T.PerspectiveCamera(36,1,.1,100);
  scene.add(new T.HemisphereLight(0xeaf4ff,0x766351,2.6));
  const key=new T.DirectionalLight(0xfff1e5,2.8);key.position.set(-4,6,8);scene.add(key);
  const edge=new T.DirectionalLight(0x8ebcff,1.5);edge.position.set(4,2,-3);scene.add(edge);
  const loader=new T.TextureLoader();
  const results=await Promise.allSettled(artwork.map(flavour=>loader.loadAsync(artworkUrls[flavour.src])));
  textures=results.filter(r=>r.status==='fulfilled').map(r=>r.value);
  if(disposed||results.some(r=>r.status==='rejected')){textures.forEach(t=>t.dispose());if(disposed)return;throw new Error('Product artwork did not load');}
  textures.forEach(t=>{t.colorSpace=T.SRGBColorSpace;t.anisotropy=Math.min(8,renderer.capabilities.getMaxAnisotropy());});
  allModels=Array.from({length:artwork.length},(_,i)=>{
   const item=artwork[i%artwork.length];
   const model=createCarton(T,textures[i%artwork.length],item.profile);
   model.name=item.name;model.userData.flavour=item.id;scene.add(model);return model;
  });
  models=featuredFirst(allModels,selected);
  root.dataset.flavours=models.map(model=>model.userData.flavour).join(',');
  ready=true;loading=false;sync();resize();
 }catch(error){loading=false;if(!disposed)fail(error);}
}
function schedule(){if(!raf&&live()&&visible&&!document.hidden&&!disposed)raf=requestAnimationFrame(draw);}
function draw(time){
 raf=0;if(!live()||!visible||document.hidden||disposed)return;
 const dt=lastTime?Math.min(.06,(time-lastTime)/1000):1/60;lastTime=time;
 const {start,span}=bounds();
 const target=clamp((scrollY-start)/span),damping=1-Math.exp(-13*dt);
 current+= (target-current)*damping;if(Math.abs(current-target)<.00012)current=target;
 pointer.x+=(pointerTarget.x-pointer.x)*damping;pointer.y+=(pointerTarget.y-pointer.y)*damping;
 const small=matchMedia('(max-width:800px)').matches;
 const sample=poses(current,small,models.length-1);
 models.forEach((model,i)=>{
  const value=sample[i];model.visible=value.visible;model.position.set(...value.position);model.scale.setScalar(value.scale);
  // Aim toward the camera, then add the designed offset, keeping the verified
  // front/top/left faces in view as each carton travels through the scene.
  const yaw=Math.atan2(-model.position.x,camera.position.z-model.position.z);
  const pitch=-Math.atan2(-model.position.y,camera.position.z-model.position.z);
  model.rotation.set(value.rotation[0]+pitch+pointer.y*.018,value.rotation[1]+yaw+pointer.x*.022,value.rotation[2]);
 });
 wash.style.opacity=String(smooth(current,.55,1)*.9);
 word.style.transform=`translate3d(${-current*4}%,${current*9}%,0)`;
 progress.style.transform=`scaleX(${current})`;
 const limits=[.21,.42,.82];
 setChapter(limits.filter(value=>current>=value).length);
 renderer.render(scene,camera);root.dataset.progress=current.toFixed(3);root.dataset.draws=String((Number(root.dataset.draws)||0)+1);
 if(Math.abs(current-target)>.00012||Math.abs(pointer.x-pointerTarget.x)>.001||Math.abs(pointer.y-pointerTarget.y)>.001)schedule();
}
flavours.forEach((button,i)=>button.addEventListener('click',()=>{
 selected=i;flavours.forEach((b,j)=>b.setAttribute('aria-pressed',String(i===j)));
 if(models.length){
  models=featuredFirst(allModels,i);
  root.dataset.flavours=models.map(model=>model.userData.flavour).join(',');
 }
 poster.src=artworkUrls[artwork[i].src];
  poster.alt=`LiteBite ${i?'Hazelnut Fudge':'Chocolate Brownies'} carton`;
  const link=root.querySelector('.shop');link.href=button.dataset.url;link.textContent=`Shop ${i?'Hazelnut':'Brownie'} ↗`;schedule();
},{signal}));
motion?.addEventListener('click',()=>{
 off=!off;try{sessionStorage.setItem('lb-concept-motion',off?'off':'on');}catch{}
 current=0;sync();const {start}=bounds();scrollTo({top:start,behavior:'instant'});load();
},{signal});
reduced.addEventListener('change',()=>{sync();load();},{signal});
window.addEventListener('scroll',schedule,{passive:true,signal});window.addEventListener('resize',resize,{passive:true,signal});
stage.addEventListener('pointermove',event=>{
 if(event.pointerType==='touch')return;const rect=stage.getBoundingClientRect();
 pointerTarget={x:(event.clientX-rect.left)/rect.width-.5,y:(event.clientY-rect.top)/rect.height-.5};schedule();
},{passive:true,signal});
stage.addEventListener('pointerleave',()=>{pointerTarget={x:0,y:0};schedule();},{signal});
document.addEventListener('visibilitychange',()=>{lastTime=0;if(!document.hidden)schedule();},{signal});
canvas.addEventListener('webglcontextlost',event=>{event.preventDefault();fail(new Error('WebGL context lost'));},{signal});
const observer=new IntersectionObserver(([entry])=>{visible=entry.isIntersecting;if(visible){load();schedule();}else{cancelAnimationFrame(raf);raf=0;lastTime=0;}},{rootMargin:'100px'});observer.observe(root);
const sizeObserver=new ResizeObserver(resize);sizeObserver.observe(canvas);
root.orbitCleanup=()=>{
 disposed=true;aborter.abort();observer.disconnect();sizeObserver.disconnect();cancelAnimationFrame(raf);if(scene)disposeScene(scene);textures.forEach(t=>t.dispose());renderer?.dispose();
};
window.addEventListener('pagehide',event=>{if(!event.persisted)root.orbitCleanup();},{signal});
window.addEventListener('pageshow',()=>{lastTime=0;schedule();},{signal});sync();
}
function init(scope=document){scope.querySelectorAll('[data-orbit-root]').forEach(setup);}
init();
document.addEventListener('shopify:section:load',event=>init(event.target));
document.addEventListener('shopify:section:unload',event=>event.target.querySelectorAll('[data-orbit-root]').forEach(root=>root.orbitCleanup?.()));
